import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import BotonVolver from "../../../components/BotonVolver";

// Module-level map to avoid duplicate identical fetches across remounts
const ongoingSeguimientoCompradorFetches = new Map();

// Reutiliza helpers de SeguimientoRepartidores (copiados aquí para simplicidad)
const parseDateSafe = (s) => {
  if (!s) return null;
  let d = new Date(s);
  if (!isNaN(d)) return d;
  d = new Date(String(s).replace(" ", "T"));
  if (!isNaN(d)) return d;
  const t = Date.parse(String(s));
  if (!Number.isNaN(t)) return new Date(t);
  return null;
};

const fmtH = (d) => {
  if (typeof d === 'string' && /GMT\s*$/.test(d)) {
    const m = d.match(/\b(\d{2}):(\d{2}):(\d{2})\b/);
    if (m) return `${m[1]}:${m[2]}`;
  }
  const dt = parseDateSafe(d);
  if (!dt) return String(d || "");
  const hh = String(dt.getHours()).padStart(2, "0");
  const mm = String(dt.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

const getToday = () => {
  const tzNow = new Date();
  const yyyy = tzNow.getFullYear();
  const mm = String(tzNow.getMonth() + 1).padStart(2, "0");
  const dd = String(tzNow.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const fmtDur = (ms) => {
  if (ms == null) return "-";
  const s = Math.max(0, Math.floor(ms / 1000));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  if (hh > 0) return `${hh}h ${mm}m`;
  return `${mm}m`;
};

const SeguimientoCompradores = () => {
  const [compradores, setCompradores] = useState([]);
  const [comprador, setComprador] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.localStorage.getItem('segComp.comprador') || "";
    }
    return "";
  });
  const [dia, setDia] = useState(() => {
    const today = getToday();
    if (typeof window !== 'undefined') {
      return window.localStorage.getItem('segComp.dia') || today;
    }
    return today;
  });

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // data: { pendientes: [], compras: [] }
  const [data, setData] = useState({ pendientes: [], compras: [] });

  const esHoy = useMemo(() => {
    const tzNow = new Date();
    const yyyy = tzNow.getFullYear();
    const mm = String(tzNow.getMonth() + 1).padStart(2, "0");
    const dd = String(tzNow.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}` === dia;
  }, [dia]);

  // cargar compradores (endpoint existente: /compras/compradores)
  // Normalizamos siempre a un array de strings (username) para que todos los componentes
  // usen la misma forma y no dependa de si el backend devuelve strings u objetos { user }.
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`/api/compras/compradores`);
        // El backend puede devolver: {compradores: [...] } o un array directo; cada elemento
        // puede ser string o objeto { user, username, name, ... } — normalizamos robustamente.
        const list = Array.isArray(res.data?.compradores) ? res.data.compradores : (Array.isArray(res.data) ? res.data : []);
        const rows = list
          .map((v) => {
            if (!v) return null;
            if (typeof v === 'string') {
              const s = v.trim();
              return s || null;
            }
            // Try common fields
            const candidate = v.user ?? v.username ?? v.name ?? v.user_comprador ?? v.id ?? v.userId;
            if (candidate != null) {
              const s = String(candidate).trim();
              if (s && s !== 'null' && s !== 'undefined') return s;
            }
            // Fallback: find any non-empty string property
            for (const k of Object.keys(v)) {
              const vv = v[k];
              if (typeof vv === 'string' && vv.trim()) return vv.trim();
            }
            return null;
          })
          .filter(Boolean);
        setCompradores(rows);
      } catch (e) { /* noop */ }
    })();
  }, []);

  // Persistir filtros en localStorage y limpiar on unmount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('segComp.comprador', comprador || "");
      window.localStorage.setItem('segComp.dia', dia || getToday());
    } catch (e) { /* ignore */ }
    return () => {
      try {
        window.localStorage.removeItem('segComp.comprador');
        window.localStorage.removeItem('segComp.dia');
      } catch (e) { /* ignore */ }
    };
  }, [comprador, dia]);

  const fetchData = async () => {
    const key = `${comprador}::${dia}`;
    console.log('[SeguimientoCompradores] fetchData called', { comprador, dia, key });
    if (!comprador || !dia) {
      console.log('[SeguimientoCompradores] fetchData aborted: missing comprador or dia');
      return;
    }

    if (ongoingSeguimientoCompradorFetches.has(key)) {
      console.log('[SeguimientoCompradores] fetchData skipping because identical fetch in progress', key);
      return;
    }
    ongoingSeguimientoCompradorFetches.set(key, true);
    setLoading(true); setErr("");
    try {
      // Endpoint asumido: /compras/seguimiento_comprador?user_comprador=...&dia=...
      const r = await axios.get(`/api/compras/seguimiento_comprador`, {
        params: { user_comprador: comprador, dia }
      });
      // Normalizar respuesta: intentamos varias keys
      const payload = r.data || {};
      const pendientes = payload.pendientes || payload.compras_pendientes || payload.preparando || [];
      const compras = payload.compras || payload.entregas || payload.lista || [];
      setData({ pendientes, compras });
      console.log('[SeguimientoCompradores] fetchData success', { pendientes: pendientes.length, compras: compras.length });
    } catch (e) {
      setErr("Error cargando seguimiento comprador");
      setData({ pendientes: [], compras: [] });
    } finally {
      ongoingSeguimientoCompradorFetches.delete(key);
      setLoading(false);
    }
  };

  useEffect(() => {
    const run = async () => {
        const y = (typeof window !== 'undefined') ? window.scrollY : 0;
        await fetchData();
        if (typeof window !== 'undefined') {
          window.requestAnimationFrame(() => setTimeout(() => window.scrollTo(0, y), 0));
        }
    };
    run();
    // eslint-disable-next-line
  }, [comprador, dia]);

  const handleRefresh = async () => {
    console.log('[SeguimientoCompradores] manual refresh triggered');
    const y = typeof window !== 'undefined' ? window.scrollY : 0;
    await fetchData();
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => setTimeout(() => window.scrollTo(0, y), 0));
    }
  };

  // Agrupar compras y calcular duraciones entre compras (entre dia_compra prev->curr)
  const comprasConDur = useMemo(() => {
    const arr = Array.isArray(data.compras) ? data.compras.slice() : [];
    // Parsear fecha para orden
    arr.forEach(c => { c._t = parseDateSafe(c.dia_compra); });
    arr.sort((a,b) => (a._t || 0) - (b._t || 0));
    const elements = [];
    let prevTime = null;
    for (let i = 0; i < arr.length; i++) {
      const c = arr[i];
      // Si hay prevTime, insertar un objeto intermedio con diff
      if (prevTime && c._t) {
        const diff = Math.max(0, c._t - prevTime);
        elements.push({ __type: 'dur', diff });
      }
      elements.push({ __type: 'compra', data: c });
      prevTime = c._t || prevTime;
    }
    return elements;
  }, [data.compras]);

  return (
    <div style={styles.container}>
      <BotonVolver fallback="/admin" />
      <h1 style={styles.title}>Seguimiento Compradores</h1>

      <div style={styles.filtersCard}>
        <div style={styles.filters}>
          <div style={styles.filter}>
            <label style={styles.label}>Comprador</label>
            <select style={styles.inputCtl} value={comprador} onChange={(e) => setComprador(e.target.value)}>
              <option value="">-- Selecciona --</option>
                {compradores.map((r, idx) => {
                  // ya normalizamos a strings; pero por seguridad calculamos una etiqueta segura
                  const val = (typeof r === 'string' ? r : String(r || '')).trim();
                  const label = val || `(sin nombre ${idx + 1})`;
                  return <option key={`${val}-${idx}`} value={val}>{label}</option>;
                })}
            </select>
          </div>
          <div style={styles.filter}>
            <label style={styles.label}>Día</label>
            <input style={styles.inputCtl} type="date" value={dia} onChange={(e) => setDia(e.target.value)} />
          </div>
        </div>
      </div>

      {comprador && dia && (() => {
        const comprasArray = data.compras || [];
        const comprasRuta12 = comprasArray.filter(c => c.ruta === 1 || c.ruta === 2 || c.ruta === '1' || c.ruta === '2');
        const comprasRuta3 = comprasArray.filter(c => c.ruta === 3 || c.ruta === '3');

        const comprasConDurR12 = (() => {
          const arr = comprasRuta12.slice();
          arr.forEach(c => { c._t = parseDateSafe(c.dia_compra); });
          arr.sort((a,b) => (a._t || 0) - (b._t || 0));
          const elements = [];
          let prevTime = null;
          for (let i = 0; i < arr.length; i++) {
            const c = arr[i];
            if (prevTime && c._t) { elements.push({ __type: 'dur', diff: Math.max(0, c._t - prevTime) }); }
            elements.push({ __type: 'compra', data: c });
            prevTime = c._t || prevTime;
          }
          return elements;
        })();

        const comprasConDurR3 = (() => {
          const arr = comprasRuta3.slice();
          arr.forEach(c => { c._t = parseDateSafe(c.dia_compra); });
          arr.sort((a,b) => (a._t || 0) - (b._t || 0));
          const elements = [];
          let prevTime = null;
          for (let i = 0; i < arr.length; i++) {
            const c = arr[i];
            if (prevTime && c._t) { elements.push({ __type: 'dur', diff: Math.max(0, c._t - prevTime) }); }
            elements.push({ __type: 'compra', data: c });
            prevTime = c._t || prevTime;
          }
          return elements;
        })();

        const tiempoTotalR12 = comprasConDurR12.filter(el => el.__type === 'dur').reduce((sum, el) => sum + el.diff, 0);
        const tiempoTotalR3 = comprasConDurR3.filter(el => el.__type === 'dur').reduce((sum, el) => sum + el.diff, 0);

        return (
          <div style={styles.subheaderCard}>
            <div style={styles.subheaderBig}>Compras</div>
            <div style={styles.subheaderLine}><b style={{ fontSize: 22 }}>{comprador}</b> — <span style={{ fontSize: 18 }}>{dia}</span></div>
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ background: '#f8fafc', padding: '12px', borderRadius: 10, border: '1px solid #cbd5e1' }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>Ruta 1/2</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 16, color: '#334155', background: '#fef3c7', padding: '8px 12px', borderRadius: 8, border: '1px solid #f59e0b' }}>
                    <span style={{ fontWeight: 600 }}>Compras totales:</span> <span style={{ fontWeight: 800, color: '#1e293b' }}>{comprasRuta12.length}</span>
                  </div>
                  <div style={{ fontSize: 16, color: '#334155', background: '#dbeafe', padding: '8px 12px', borderRadius: 8, border: '1px solid #3b82f6' }}>
                    <span style={{ fontWeight: 600 }}>Tiempo total:</span> <span style={{ fontWeight: 800, color: '#1e293b' }}>{fmtDur(tiempoTotalR12)}</span>
                  </div>
                  <div style={{ fontSize: 16, color: '#334155', background: '#dcfce7', padding: '8px 12px', borderRadius: 8, border: '1px solid #10b981' }}>
                    <span style={{ fontWeight: 600 }}>Tiempo medio:</span> <span style={{ fontWeight: 800, color: '#1e293b' }}>{comprasRuta12.length > 0 ? fmtDur(tiempoTotalR12 / comprasRuta12.length) : 'N/A'}</span>
                  </div>
                  <div style={{ fontSize: 16, color: '#334155', background: '#fce7f3', padding: '8px 12px', borderRadius: 8, border: '1px solid #ec4899' }}>
                    <span style={{ fontWeight: 600 }}>Precio medio:</span> <span style={{ fontWeight: 800, color: '#1e293b' }}>{comprasRuta12.length > 0 ? (comprasRuta12.reduce((s, c) => s + parseFloat(c.precio_total || 0), 0) / comprasRuta12.length).toFixed(2) + ' €' : 'N/A'}</span>
                  </div>
                </div>
              </div>
              <div style={{ background: '#f8fafc', padding: '12px', borderRadius: 10, border: '1px solid #cbd5e1' }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>Ruta 3</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 16, color: '#334155', background: '#fef3c7', padding: '8px 12px', borderRadius: 8, border: '1px solid #f59e0b' }}>
                    <span style={{ fontWeight: 600 }}>Compras totales:</span> <span style={{ fontWeight: 800, color: '#1e293b' }}>{comprasRuta3.length}</span>
                  </div>
                  <div style={{ fontSize: 16, color: '#334155', background: '#dbeafe', padding: '8px 12px', borderRadius: 8, border: '1px solid #3b82f6' }}>
                    <span style={{ fontWeight: 600 }}>Tiempo total:</span> <span style={{ fontWeight: 800, color: '#1e293b' }}>{fmtDur(tiempoTotalR3)}</span>
                  </div>
                  <div style={{ fontSize: 16, color: '#334155', background: '#dcfce7', padding: '8px 12px', borderRadius: 8, border: '1px solid #10b981' }}>
                    <span style={{ fontWeight: 600 }}>Tiempo medio:</span> <span style={{ fontWeight: 800, color: '#1e293b' }}>{comprasRuta3.length > 0 ? fmtDur(tiempoTotalR3 / comprasRuta3.length) : 'N/A'}</span>
                  </div>
                  <div style={{ fontSize: 16, color: '#334155', background: '#fce7f3', padding: '8px 12px', borderRadius: 8, border: '1px solid #ec4899' }}>
                    <span style={{ fontWeight: 600 }}>Precio medio:</span> <span style={{ fontWeight: 800, color: '#1e293b' }}>{comprasRuta3.length > 0 ? (comprasRuta3.reduce((s, c) => s + parseFloat(c.precio_total || 0), 0) / comprasRuta3.length).toFixed(2) + ' €' : 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {loading && <div>Cargando…</div>}
      {err && <div style={styles.error}>{err}</div>}

      {!loading && comprador && dia && (
        <div style={styles.block}>
          <h2 style={styles.sectionTitle}>Compras pendientes</h2>
          {Array.isArray(data.pendientes) && data.pendientes.length > 0 ? (
            <div>
              <div style={styles.countsRow}>
                <span style={{...styles.countBadge, background:'#eef2ff', borderColor:'#93c5fd', color:'#1e3a8a'}}>Pendientes: {data.pendientes.length}</span>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {data.pendientes.map((c, i) => {
                  const rutaColor = c.ruta === 1 || c.ruta === '1' ? '#4ECDC4' : c.ruta === 2 || c.ruta === '2' ? '#4CAF50' : '#556270';
                  return (
                    <li key={c.id||i} style={{ padding: '8px 10px', borderBottom: '1px solid #eee', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ fontWeight: 800 }}>{c.abreviatura_piso || ''}</div>
                      <div style={{ color: '#64748b', fontWeight: 700 }}>{c.franja_horaria ? `[${c.franja_horaria}]` : ''}</div>
                      <span style={{ color: '#fff', background: rutaColor, padding: '2px 8px', borderRadius: 12, fontSize: 13, fontWeight: 800 }}>Ruta {c.ruta || '?'}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <div>No hay compras pendientes</div>
          )}
        </div>
      )}

      {!loading && comprador && dia && (
        <div style={{ ...styles.block, marginTop: 16, background: '#f9fafb' }}>
          <h2 style={styles.sectionTitle}>Timeline de compras</h2>
          <div style={styles.timelineWrap}>
            <div style={styles.verticalLine} />
            {Array.isArray(comprasConDur) && comprasConDur.length > 0 ? (
              comprasConDur.map((el, idx) => {
                if (el.__type === 'dur') {
                  return (
                    <div key={`dur-${idx}`} style={{ ...styles.betweenRow }}>
                      <div style={styles.durationBetween}>⏱ {fmtDur(el.diff)}</div>
                    </div>
                  );
                }
                const c = el.data;
                const rutaColor = c.ruta === 1 || c.ruta === '1' ? '#4ECDC4' : c.ruta === 2 || c.ruta === '2' ? '#4CAF50' : '#556270';
                return (
                  <div key={c.id||idx} style={{ ...styles.timelineItem, display:'flex', alignItems: 'center', marginLeft: -32 }}>
                    <div style={{ ...styles.dot, margin: '0 18px', position: 'relative', left: 0 }} />
                    <span style={{ ...styles.time, marginRight: 10 }}>{fmtH(c.dia_compra)}</span>
                    <div style={styles.itemContent}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={styles.itemLineRow}>
                          <span style={styles.piso}><b>{c.abreviatura_piso || ''}</b> <span style={{ color: '#64748b', fontWeight:700 }}>{c.franja_horaria ? `[${c.franja_horaria}]` : ''}</span></span>
                        </div>
                        <div>
                          <span style={{ color: '#fff', background: rutaColor, padding: '2px 8px', borderRadius: 12, fontSize: 13, fontWeight: 800 }}>Ruta {c.ruta || '?'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ padding: 8 }}>No hay compras en el timeline</div>
            )}
          </div>
        </div>
      )}

      <button style={styles.fabRefresh} onClick={handleRefresh} title="Actualizar">⟳ Actualizar</button>
    </div>
  );
};

const styles = {
  container: { padding: 20, maxWidth: 900, margin: "0 auto" },
  title: { marginBottom: 20 },
  filtersCard: { border:'1px solid #e5e7eb', borderRadius:12, padding:12, background:'#f0f9ff', marginBottom:16 },
  filters: { display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" },
  filter: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontWeight:700, fontSize:13, color:'#334155' },
  inputCtl: { padding:'8px 12px', border:'1px solid #cbd5e1', borderRadius:8, fontSize:14, outline:'none' },
  refresh: { padding: "8px 12px" },
  primary: { padding: "10px 14px", background: "#007bff", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" },
  subheader: { marginTop: 12, fontSize: 18, color:'#111827', fontWeight:800 },
  subheaderCard: { border:'1px solid #e5e7eb', borderRadius:12, padding:'14px 16px', background:'#e0f2fe', marginBottom:16, boxShadow:'0 1px 2px rgba(16,24,40,0.04)' },
  subheaderBig: { fontSize: 26, lineHeight: '28px', fontWeight: 900, color:'#0f172a', marginBottom: 6 },
  subheaderLine: { fontSize: 18, color:'#334155', fontWeight:700 },
  error: { color: "red", marginBottom: 10 },
  timeline: { display: "grid", gap: 16 },
  block: { border: "1px solid #ddd", borderRadius: 8, padding: 12, background: '#e0f2fe' },
  sectionTitle: { margin:0, marginBottom:8, fontSize:24 },
  badgeList: { marginTop: 8, color: "#333", display: 'flex', flexWrap: 'wrap', gap: 6 },
  badge: { border: '1px solid #ccc', borderRadius: 16, padding: '4px 10px', background: '#f7f7f7' },
  timelineWrap: { position: 'relative', paddingLeft: 34, paddingTop: 8 },
  verticalLine: { position: 'absolute', top: 0, bottom: 0, left: 16, width: 3, background: 'linear-gradient(#94a3b8, #cbd5e1)' },
  timelineItem: { position: 'relative', marginBottom: 14 },
  dot: { position: 'absolute', left: 11, top: 4, width: 12, height: 12, borderRadius: '50%', background: '#0ea5e9', border: '2px solid #0369a1', boxShadow:'0 0 0 3px rgba(14,165,233,0.15)' },
  itemContent: { marginLeft: 16, paddingLeft: 16 },
  itemHeader: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  itemLineRow: { display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' },
  time: { fontWeight: 800, color: '#0f172a' },
  piso: { color: '#1f2937' },
  estado: { fontWeight: 700 },
  durationBetween: { marginTop: 6, color: '#555', background: '#f1f5f9', display: 'inline-block', padding: '2px 8px', borderRadius: 12, border: '1px solid #e2e8f0' },
  betweenRow: { position:'relative', marginLeft: 16, paddingLeft: 16, marginTop: 6, marginBottom: 16, display:'flex', alignItems:'center', minHeight: 20 },
  countsRow: { display:'flex', gap:8, flexWrap:'wrap', margin:'6px 0 12px' },
  countBadge: { border:'1px solid', borderRadius:14, padding:'6px 12px', fontWeight:800, fontSize:15 },
  fabRefresh: { position:'fixed', right:16, bottom:16, zIndex:1000, background:'#007bff', color:'#fff', border:'none', borderRadius:999, padding:'12px 18px', fontSize:16, boxShadow:'0 8px 16px rgba(0,0,0,0.15)', cursor:'pointer', fontWeight:900 }
};

export default SeguimientoCompradores;
